import { Fragment, useEffect, useRef, useState } from "react";
import useSWR, { SWRConfig } from "swr";
import { Download, FileClock, Info, RefreshCw } from "lucide-react";
import { useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization";
import { useOrganizationScopes } from "@/lib/scopes";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { components } from "@/client/backend/zz_schema";

type Event = components["schemas"]["AuditLogEvent"];
type Query = {
	start: string;
	end: string;
	action?: string;
	actorId?: string;
	resourceId?: string;
	outcome?: Exclude<Filters["outcome"], "">;
	limit: number;
	cursor?: string;
};
const pageCache = () => new Map();

type Filters = {
	start: string;
	end: string;
	action: string;
	actorId: string;
	resourceId: string;
	outcome: "" | "success" | "accepted" | "denied" | "failure" | "unknown";
};

function localDate(date: Date) {
	return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
function initialFilters(): Filters {
	const now = new Date();
	return {
		start: localDate(new Date(now.getTime() - 60 * 60_000)),
		end: localDate(now),
		action: "",
		actorId: "",
		resourceId: "",
		outcome: "",
	};
}

export function AuditLogs() {
	const [organization] = useOrganization();
	const { role, loaded } = useOrganizationScopes(organization);
	return (
		<AppShell title="Audit logs">
			<PageHeader
				title="Audit logs"
				description={
					<span role="note" className="inline-flex items-center gap-1.5 text-xs">
						<Info className="size-3.5 shrink-0" aria-hidden="true" />
						New events may take about 5 minutes to appear.
					</span>
				}
			/>
			{!loaded || organization === "loading" ? (
				<p role="status">Loading your organization...</p>
			) : role !== "admin" ? (
				<EmptyState
					icon={<FileClock />}
					title="Organization admins only"
					description="Ask an organization admin to review the audit logs."
				/>
			) : (
				<TooltipProvider delayDuration={150}>
					<AuditLogSearch key={organization.tid} organizationId={organization.tid} />
				</TooltipProvider>
			)}
		</AppShell>
	);
}

function AuditLogSearch({ organizationId }: { organizationId: string }) {
	const [draft, setDraft] = useState(initialFilters);
	const [filters, setFilters] = useState(draft);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState(0);
	const [validation, setValidation] = useState<string>();
	const query = {
		start: new Date(filters.start).toISOString(),
		end: new Date(filters.end).toISOString(),
		action: filters.action || undefined,
		actorId: filters.actorId || undefined,
		resourceId: filters.resourceId || undefined,
		outcome: filters.outcome || undefined,
		limit: pageSize,
	};
	function submit(event: React.FormEvent) {
		event.preventDefault();
		const start = new Date(draft.start).getTime(),
			end = new Date(draft.end).getTime();
		if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 7 * 24 * 60 * 60_000) {
			setValidation("Choose a start and end time no more than 7 days apart.");
			return;
		}
		setValidation(undefined);
		setFilters({ ...draft });
		setSearch(search + 1);
	}
	function change(name: keyof Filters, value: string) {
		setDraft((current) => ({ ...current, [name]: value }));
	}
	return (
		<div className="space-y-5">
			<form onSubmit={submit} className="space-y-3">
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					<div className="space-y-1">
						<Label htmlFor="audit-start">From</Label>
						<Input
							id="audit-start"
							type="datetime-local"
							required
							value={draft.start}
							onChange={(e) => change("start", e.target.value)}
						/>
					</div>
					<div className="space-y-1">
						<Label htmlFor="audit-end">To</Label>
						<Input
							id="audit-end"
							type="datetime-local"
							required
							value={draft.end}
							onChange={(e) => change("end", e.target.value)}
						/>
					</div>
					<div className="space-y-1">
						<Label htmlFor="audit-outcome">Outcome</Label>
						<Select
							value={draft.outcome || "all"}
							onValueChange={(value) => change("outcome", value === "all" ? "" : value)}
						>
							<SelectTrigger id="audit-outcome">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All outcomes</SelectItem>
								{["success", "accepted", "denied", "failure", "unknown"].map((value) => (
									<SelectItem key={value} value={value}>
										{value}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					{(
						[
							["action", "Action"],
							["actorId", "Actor ID"],
							["resourceId", "Resource ID"],
						] as const
					).map(([name, label]) => (
						<div key={name} className="space-y-1">
							<Label htmlFor={`audit-${name}`}>{label}</Label>
							<Input
								id={`audit-${name}`}
								value={draft[name]}
								maxLength={128}
								placeholder="Any"
								onChange={(e) => change(name, e.target.value)}
							/>
						</div>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-3">
					<Button type="submit">Search</Button>
					<Button type="button" variant="outline" onClick={() => setSearch(search + 1)}>
						<RefreshCw className="mr-2 h-4 w-4" />
						Refresh
					</Button>
					<AuditExport organizationId={organizationId} filters={draft} />
					<p className="text-xs text-muted-foreground">
						Times are local. Search up to 7 days at a time. Text filters match exact values.
					</p>
				</div>
				{validation && (
					<p role="alert" className="text-sm text-destructive">
						{validation}
					</p>
				)}
			</form>
			<SWRConfig key={search} value={{ provider: pageCache }}>
				<AuditPages
					organizationId={organizationId}
					query={query}
					onPageSizeChange={(size) => {
						setPageSize(size);
						setSearch(search + 1);
					}}
				/>
			</SWRConfig>
		</div>
	);
}

function AuditExport({ organizationId, filters }: { organizationId: string; filters: Filters }) {
	const auth = useAuth();
	const [open, setOpen] = useState(false);
	const [start, setStart] = useState(filters.start);
	const [end, setEnd] = useState(filters.end);
	const [format, setFormat] = useState<"csv" | "json">("csv");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string>();
	const active = useRef<AbortController | null>(null);
	useEffect(() => () => active.current?.abort(), []);
	function changeOpen(value: boolean) {
		if (!value) active.current?.abort();
		else {
			setStart(filters.start);
			setEnd(filters.end);
			setError(undefined);
		}
		setOpen(value);
	}
	async function download() {
		const from = new Date(start).getTime(), to = new Date(end).getTime();
		if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from || to - from > 30 * 24 * 60 * 60_000) {
			setError("Choose a start and end time no more than 30 days apart.");
			return;
		}
		if (active.current) return;
		const controller = new AbortController();
		active.current = controller;
		// Allow the server to return its 120-second timeout response before aborting transport.
		const timer = window.setTimeout(() => controller.abort("timeout"), 135_000);
		setPending(true);
		setError(undefined);
		try {
			const result = await auth.client.GET("/organizations/{organizationId}/audit_logs/export", {
				params: { path: { organizationId }, query: {
					start: new Date(from).toISOString(), end: new Date(to).toISOString(), format,
					action: filters.action || undefined, actorId: filters.actorId || undefined,
					resourceId: filters.resourceId || undefined, outcome: filters.outcome || undefined,
				} },
				parseAs: "blob", cache: "no-store", signal: controller.signal,
			});
			if (controller.signal.aborted) return;
			if (result.error || !result.data) throw new Error(result.error?.message ?? "Could not export audit logs");
			const url = URL.createObjectURL(result.data);
			const link = document.createElement("a");
			link.href = url;
			link.download = `audit-logs-${new Date(from).toISOString().slice(0, 10)}-to-${new Date(to).toISOString().slice(0, 10)}.${format}`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.setTimeout(() => URL.revokeObjectURL(url), 1000);
			setOpen(false);
		} catch (cause) {
			if (controller.signal.reason === "timeout") setError("Audit export timed out. Try a shorter time range.");
			else if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not export audit logs");
		} finally {
			window.clearTimeout(timer);
			active.current = null;
			setPending(false);
		}
	}
	return (
		<Dialog open={open} onOpenChange={changeOpen}>
			<DialogTrigger asChild>
				<Button type="button" variant="outline"><Download className="mr-2 size-4" />Export</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Export audit logs</DialogTitle>
					<DialogDescription>Export up to 30 days. Uses your current action, actor, resource and outcome filters. Times are local.</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-1">
						<Label htmlFor="audit-export-start">Export from</Label>
						<Input id="audit-export-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} disabled={pending} />
					</div>
					<div className="space-y-1">
						<Label htmlFor="audit-export-end">Export to</Label>
						<Input id="audit-export-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} disabled={pending} />
					</div>
					<div className="space-y-1 sm:col-span-2">
						<Label htmlFor="audit-export-format">Format</Label>
						<Select value={format} onValueChange={(value) => setFormat(value as "csv" | "json")} disabled={pending}>
							<SelectTrigger id="audit-export-format"><SelectValue /></SelectTrigger>
							<SelectContent><SelectItem value="csv">CSV</SelectItem><SelectItem value="json">JSON</SelectItem></SelectContent>
						</Select>
					</div>
				</div>
				{pending && <p role="status" className="text-sm text-muted-foreground">Preparing your export. This may take up to 2 minutes.</p>}
				{error && <p role="alert" className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => changeOpen(false)}>Cancel</Button>
					<Button type="button" disabled={pending} onClick={() => void download()}>{pending ? "Preparing..." : "Download"}</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function AuditPages({
	organizationId,
	query,
	onPageSizeChange,
}: {
	organizationId: string;
	query: Query;
	onPageSizeChange: (size: number) => void;
}) {
	const auth = useAuth();
	const [cursor, setCursor] = useState<string>();
	const [history, setHistory] = useState<(string | undefined)[]>([]);
	const [expanded, setExpanded] = useState<string>();
	const requests = useRef(new Set<AbortController>());
	useEffect(() => {
		const active = requests.current;
		return () => {
			for (const request of active) request.abort();
			active.clear();
		};
	}, []);
	const fetchPage = async ([, org, params]: [string, string, Query]) => {
		const controller = new AbortController();
		requests.current.add(controller);
		try {
			const result = await auth.client.GET("/organizations/{organizationId}/audit_logs", {
				params: { path: { organizationId: org }, query: params },
				cache: "no-store",
				signal: controller.signal,
			});
			if (result.error || !result.data) throw new Error(result.error?.message ?? "Could not load audit logs");
			return result.data;
		} finally {
			requests.current.delete(controller);
		}
	};
	const options = {
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		revalidateIfStale: false,
		shouldRetryOnError: false,
	};
	const { data, error, isLoading, mutate } = useSWR(["audit-logs", organizationId, { ...query, cursor }], fetchPage, {
		...options,
		keepPreviousData: true,
	});
	// Read one page ahead; navigating back uses this search's in-memory cache.
	const { error: prefetchError, mutate: retryPrefetch } = useSWR(
		!isLoading && !error && data?.nextCursor
			? ["audit-logs", organizationId, { ...query, cursor: data.nextCursor }]
			: null,
		fetchPage,
		options,
	);
	const pageError = isLoading ? undefined : error;
	const rows = pageError ? [] : data?.events;
	const offset = history.length * query.limit;
	return (
		<div className="space-y-3">
			{pageError ? (
				<div role="alert" className="space-y-2">
					<p>{pageError.message}</p>
					<Button variant="outline" onClick={() => void mutate()}>
						Try again
					</Button>
				</div>
			) : isLoading && !data ? (
				<p role="status">Searching the audit archive...</p>
			) : rows?.length === 0 ? (
				<EmptyState
					icon={<FileClock />}
					title="No audit events found"
					description="Try another time range or remove filters. Recent activity may still be uploading."
				/>
			) : (
				<div className="overflow-x-auto" aria-busy={isLoading}>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Time</TableHead>
								<TableHead>Action</TableHead>
								<TableHead>Actor</TableHead>
								<TableHead>Resource</TableHead>
								<TableHead>Outcome</TableHead>
								<TableHead>
									<span className="sr-only">Details</span>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows?.map((event) => (
								<Fragment key={event.eventId}>
									<TableRow
										className="cursor-pointer"
										onClick={(click) => {
											if ((click.target as HTMLElement).closest("button, a") || window.getSelection()?.toString()) return;
											setExpanded(expanded === event.eventId ? undefined : event.eventId);
										}}
									>
										<TableCell className="whitespace-nowrap text-xs">
											{new Date(event.occurredAt).toLocaleString()}
										</TableCell>
										<TableCell>{event.action}</TableCell>
										<TableCell className="max-w-64 text-xs">
											<div className="flex items-center gap-1">
												<span className="truncate" title={event.actor}>{event.actor}</span>
												{event.actorId && (event.actorType === "credential" || event.actorType === "user") && (
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																type="button"
																variant="ghost"
																size="icon"
																className="h-6 w-6 shrink-0 text-muted-foreground"
																aria-label={`${event.actorType === "credential" ? "API key" : "User"} ID for ${event.actor}`}
															>
																<Info aria-hidden="true" />
															</Button>
														</TooltipTrigger>
														<TooltipContent className="max-w-xs break-all">
															{event.actorType === "credential" ? "API key ID" : "User ID"}: <span className="font-mono">{event.actorId}</span>
														</TooltipContent>
													</Tooltip>
												)}
											</div>
										</TableCell>
										<TableCell className="max-w-56 truncate font-mono text-xs" title={event.resourceId}>
											{event.resourceId ?? event.resourceType}
										</TableCell>
										<TableCell>{event.outcome}</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="sm"
												aria-expanded={expanded === event.eventId}
												aria-label={`Details for ${event.action} at ${event.occurredAt}`}
												onClick={() => setExpanded(expanded === event.eventId ? undefined : event.eventId)}
											>
												Details
											</Button>
										</TableCell>
									</TableRow>
									{expanded === event.eventId && (
										<TableRow>
											<TableCell colSpan={6}>
												<EventDetails event={event} />
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							))}
						</TableBody>
					</Table>
				</div>
			)}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<span className="text-xs text-muted-foreground" aria-live="polite">
					{isLoading
						? "Loading page..."
						: data && !error
							? `${rows?.length ? offset + 1 : 0}–${rows?.length ? offset + rows.length : 0} of ${data.total.toLocaleString()} events`
							: ""}
				</span>
				<div className="flex flex-wrap items-center gap-2">
					<Label htmlFor="audit-page-size">Rows per page</Label>
					<Select value={String(query.limit)} onValueChange={(value) => onPageSizeChange(Number(value))}>
						<SelectTrigger id="audit-page-size" className="w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{[25, 50, 100, 200].map((size) => (
								<SelectItem key={size} value={String(size)}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button
						variant="outline"
						disabled={!history.length || isLoading}
						onClick={() => {
							setCursor(history[history.length - 1]);
							setHistory(history.slice(0, -1));
							setExpanded(undefined);
						}}
					>
						Previous
					</Button>
					<Button
						variant="outline"
						disabled={!data?.nextCursor || isLoading || !!error}
						onClick={() => {
							// A background failure must not become a cached failure when the page is requested.
							if (prefetchError) void retryPrefetch(undefined, { revalidate: true, throwOnError: false });
							setHistory([...history, cursor]);
							setCursor(data?.nextCursor);
							setExpanded(undefined);
						}}
					>
						Next
					</Button>
				</div>
			</div>
		</div>
	);
}

function EventDetails({ event }: { event: Event }) {
	return (
		<dl className="grid gap-3 py-3 text-xs sm:grid-cols-2">
			{Object.entries(event)
				.filter(([, value]) => value !== null && value !== undefined)
				.map(([key, value]) => (
					<div key={key} className={key === "metadata" ? "sm:col-span-2" : ""}>
						<dt className="font-medium">
							{key === "sourceIp"
								? "Observed IP"
								: key === "cfConnectingIp"
									? "CF-Connecting-IP (reported)"
									: key
											.replace(/([A-Z])/g, " $1")
											.replace(/^./, (letter) => letter.toUpperCase())
											.replace(/\bId\b/g, "ID")
											.replace(/\bIp\b/g, "IP")
											.replace(/\bMs\b/g, "(ms)")}
						</dt>
						<dd className="mt-1 whitespace-pre-wrap break-all font-mono text-muted-foreground">
							{typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
						</dd>
					</div>
				))}
		</dl>
	);
}
